// "Continue with Google" / "Continue with Microsoft", shared by the sign-in
// and sign-up screens. Both start the identical flow — the server decides
// whether it ends in a session or a sign-up form.
import GoogleIcon from "@mui/icons-material/Google";
import MicrosoftIcon from "@mui/icons-material/Microsoft";
import { Box, Button, Chip } from "@mui/material";
import React, { useState } from "react";
import type { LoginMethod } from "../../api/auth/provider-sign-in";
import {
	type SignInProvider,
	startProviderSignIn,
} from "../../api/auth/provider-sign-in";

interface ProviderButtonProps {
	label: string;
	icon: React.ReactElement;
	lastUsed: boolean;
	disabled: boolean;
	onClick: () => void;
	dataComponent: string;
}

const ProviderButton: React.FC<ProviderButtonProps> = ({
	label,
	icon,
	lastUsed,
	disabled,
	onClick,
	dataComponent,
}) => (
	<Box sx={{ position: "relative", mt: 1.5 }}>
		<Button
			fullWidth
			variant="outlined"
			color="inherit"
			startIcon={icon}
			disabled={disabled}
			onClick={onClick}
			data-component={dataComponent}
			sx={{ textTransform: "none", justifyContent: "center", py: 1 }}
		>
			{label}
		</Button>
		{lastUsed && (
			<Chip
				label="Last used"
				size="small"
				color="primary"
				data-component="login-last-used-chip"
				sx={{
					position: "absolute",
					right: 8,
					top: "50%",
					transform: "translateY(-50%)",
					pointerEvents: "none",
				}}
			/>
		)}
	</Box>
);

interface ProviderSignInButtonsProps {
	lastUsed: LoginMethod | null;
	onError: (message: string) => void;
}

export const ProviderSignInButtons: React.FC<ProviderSignInButtonsProps> = ({
	lastUsed,
	onError,
}) => {
	const [busy, setBusy] = useState(false);

	const begin = async (provider: SignInProvider): Promise<void> => {
		setBusy(true);
		try {
			await startProviderSignIn(provider);
		} catch (err) {
			setBusy(false);
			onError(
				err instanceof Error
					? err.message
					: "Could not start sign-in with that provider."
			);
		}
	};

	return (
		<Box data-component="provider-sign-in-buttons">
			<ProviderButton
				label="Continue with Google"
				icon={<GoogleIcon />}
				lastUsed={lastUsed === "google"}
				disabled={busy}
				onClick={() => void begin("google")}
				dataComponent="login-google-button"
			/>
			<ProviderButton
				label="Continue with Microsoft"
				icon={<MicrosoftIcon />}
				lastUsed={lastUsed === "microsoft"}
				disabled={busy}
				onClick={() => void begin("microsoft")}
				dataComponent="login-microsoft-button"
			/>
		</Box>
	);
};
